<template>
  <div>
    <v-data-table
      :items="items"
      :headers="headers"
      hide-default-footer
      disable-pagination
      disable-sort
      density="compact"
      class="water-line-table data-table"
      :loading="loadingData"
      :items-per-page="-1"
    >
      <template #loader="{ isActive }">
        <v-progress-linear :active="isActive" indeterminate color="primary" />
      </template>
      <template v-slot:item="{ item, index }">
        <tr @contextmenu="show($event, item)" style="height: 43px">
          <td class="px-0">
            {{ index + 1 }}
          </td>
          <td class="px-0">
            <WaterLineRuleDropDown
              :value="item.rule_type"
              :items="getRuleTypeChoices"
              :item-disabled="
                (dropdownItem) => dropdownItem.value !== item.rule_type && dropdownItem.disabled
              "
              @action="(value) => ruleTypechanged({ item, value })"
              :class="[getDirtyClass(item, 'rule_type')]"
            />
          </td>
          <td class="px-0">
            <WaterLineRuleDropDown
              v-if="item.rule_type"
              :value="item.name"
              :items="getTopoProfilePointNameChoices({ ruleType: item.rule_type })"
              :item-disabled="
                (dropdownItem) => dropdownItem.value !== item.name && dropdownItem.disabled
              "
              @action="(value) => topoProfilePointNamedChanged({ item, value })"
              :class="[getDirtyClass(item, 'name')]"
            />
            <NotAvailableTableCell v-else />
          </td>
          <td class="px-0">
            <WaterLineRuleDropDown
              v-if="item.name"
              :value="item.line"
              :items="getLineChoices({ ruleType: item.rule_type, topoProfilePointName: item.name })"
              :item-disabled="
                (dropdownItem) => dropdownItem.value !== item.line && dropdownItem.disabled
              "
              @action="(value) => lineChanged({ item, value })"
              :class="[getDirtyClass(item, 'line')]"
            />
            <NotAvailableTableCell v-else />
          </td>
          <td class="px-0">
            <WaterLineRuleDropDown
              v-if="item.line"
              :value="item.condition"
              :items="
                getConditionChoices({
                  ruleType: item.rule_type,
                  topoProfilePointName: item.name,
                  line: item.line
                })
              "
              :item-disabled="
                (dropdownItem) => dropdownItem.value !== item.condition && dropdownItem.disabled
              "
              @action="(value) => conditionChanged({ item, value })"
              :class="[getDirtyClass(item, 'condition')]"
            />
            <NotAvailableTableCell v-else />
          </td>
          <td class="px-0">
            <WaterLineRuleDropDown
              v-if="
                item.rule_type === 'boundary' || item.rule_type === 'combination_depth_boundary'
              "
              :value="item.boundary"
              :items="waterLineRuleOptions.boundary.choices"
              @action="(value) => inputChange({ item, value, header: 'boundary' })"
              :class="[getDirtyClass(item, 'boundary')]"
            />
            <NotAvailableTableCell v-else />
          </td>
          <td class="px-0">
            <DataTableTextCell
              full-width
              edit-mode
              v-if="item.rule_type === 'depth' || item.rule_type === 'combination_depth_boundary'"
              :value="item.depth"
              type="number"
              @action="(value) => inputChange({ item, value, header: 'depth' })"
              :class="[getDirtyClass(item, 'depth')]"
            />
            <NotAvailableTableCell v-else />
          </td>
          <td class="px-0">
            <DataTableTextCell
              full-width
              edit-mode
              v-if="item.rule_type === 'bulging'"
              :value="item.bulging"
              type="number"
              @action="(value) => inputChange({ item, value, header: 'bulging' })"
              :class="[getDirtyClass(item, 'bulging')]"
            />
            <NotAvailableTableCell v-else />
          </td>
          <td class="px-0">
            <DataTableTextCell
              full-width
              edit-mode
              v-if="item.rule_type === 'pressure'"
              :value="item.pressure_drop"
              type="number"
              @action="(value) => inputChange({ item, value, header: 'pressure_drop' })"
              :class="[getDirtyClass(item, 'pressure_drop')]"
            />
            <NotAvailableTableCell v-else />
          </td>
        </tr>
      </template>
    </v-data-table>

    <TableActionMenu
      @update:show-menu="showMenu = $event"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      :add-button="false"
      @action="clickDeleteItem(selectedItem)"
    />
  </div>
</template>

<script>
import { drop } from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import WaterLineRuleDropDown from '@/components/waterlinerule/WaterLineRuleDropDown.vue'
import DirtyFieldsMixIn from '@/mixins/DirtyFieldsMixIn.vue'
import ShowActionMenuMixin from '@/mixins/ShowActionMenuMixin.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import DataTableTextCell from '@/components/userManagement/DataTableTextCell.vue'
import NotAvailableTableCell from '@/components/waterlinerule/NotAvailableTableCell.vue'

export default {
  name: 'WaterLineRuleTable',
  mixins: [DirtyFieldsMixIn, ShowActionMenuMixin],
  components: {
    NotAvailableTableCell,
    DataTableTextCell,
    TableActionMenu,
    WaterLineRuleDropDown
  },
  data: () => ({
    headers: [
      { title: '#', width: '4%' },
      { title: 'Rule type', width: '15%' },
      { title: 'Name', width: '15%' },
      { title: 'Line type', width: '15%' },
      { title: 'Condition', width: '15%' },
      { title: 'Boundary', width: '15%' },
      { title: 'Depth', width: '7%' },
      { title: 'Bulging', width: '7%' },
      { title: 'Pressure Drop', width: '7%' }
    ],
    resetPath: ['name', 'line', 'condition', 'boundary', 'depth', 'bulging', 'pressure_drop']
  }),
  computed: {
    ...mapState('waterLineRule', {
      items: (state) => state.items,
      availableMapping: (state) => state.availableMapping,
      waterLineRuleOptions: (state) => state.options,
      loadingData: (state) => state.loadingData,
      updatingData: (state) => state.updatingData
    }),
    ...mapState('project', {
      currentProject: (state) => state.currentProject
    }),
    ...mapGetters('waterLineRule', {
      getRuleTypeChoices: 'getRuleTypeChoices',
      getTopoProfilePointNameChoices: 'getTopoProfilePointNameChoices',
      getLineChoices: 'getLineChoices',
      getConditionChoices: 'getConditionChoices'
    }),
    revisionId() {
      return parseInt(this.$route.params.revisionId)
    }
  },
  watch: {
    async revisionId() {
      if (!this.loadingData) {
        this.resetState()
        await this.fetchRelatedData()
      }
    }
  },
  methods: {
    ...mapActions('waterLineRule', {
      fetchOptions: 'fetchOptions',
      fetchMapping: 'fetchMapping',
      fetchItems: 'fetchItems',
      emptyDirtyItems: 'emptyDirtyItems',
      setParameter: 'setParameter',
      addNewRule: 'addNewRule',
      setCurrentItem: 'setCurrentItem',
      deleteItem: 'deleteItem',
      update: 'update',
      create: 'create',
      toggleUpdatingData: 'toggleUpdatingData',
      updateAvailableMapping: 'updateAvailableMapping',
      toggleLoadingData: 'toggleLoadingData',
      resetState: 'resetState'
    }),
    async fetchRelatedData() {
      try {
        this.toggleLoadingData()
        await this.fetchOptions()
        await this.fetchMapping(this.revisionId)
        await this.fetchItems(this.revisionId)
      } catch (e) {
        console.log(e)
      } finally {
        this.toggleLoadingData()
      }
    },
    async saveChanges() {
      try {
        this.toggleUpdatingData()
        await this.update()
        await this.create()
        this.emptyDirtyItems()
        this.dirtyItems = []
      } catch (e) {
        console.log(e)
      } finally {
        this.toggleUpdatingData()
      }
    },
    add() {
      this.addNewRule({ revisionId: this.revisionId })
    },
    inputChange({ item, header, value }) {
      this.addDirty(item, header)
      this.setParameter({ item, value, parameter: header })
    },
    resetItemValues({ item, fields = [], resetValue = null }) {
      for (const field of fields) {
        this.setParameter({ item, value: resetValue, parameter: field })
      }
    },
    ruleTypechanged({ item, value }) {
      this.addDirty(item, 'rule_type')
      this.updateAvailableMapping({ item, isAddItem: false })
      this.inputChange({ item, header: 'rule_type', value })
      this.resetItemValues({ item, fields: this.resetPath })
    },
    topoProfilePointNamedChanged({ item, value }) {
      this.addDirty(item, 'name')
      this.updateAvailableMapping({ item, isAddItem: false })
      this.inputChange({ item, header: 'name', value })
      this.resetItemValues({ item, fields: drop(this.resetPath, 1) })
    },
    lineChanged({ item, value }) {
      this.addDirty(item, 'line')
      this.updateAvailableMapping({ item, isAddItem: false })
      this.inputChange({ item, header: 'line', value })
      this.resetItemValues({ item, fields: drop(this.resetPath, 2) })
    },
    conditionChanged({ item, value }) {
      this.addDirty(item, 'condition')
      this.inputChange({ item, header: 'condition', value })
      this.updateAvailableMapping({ item, isAddItem: true })
    },
    clickDeleteItem(item) {
      if (item.condition) {
        this.updateAvailableMapping({ item, isAddItem: false })
      }
      this.deleteItem(item.id)
    }
  },
  async mounted() {
    if (this.revisionId) {
      this.resetState()
      await this.fetchRelatedData()
    }
  }
}
</script>

<style scoped>
.water-line-table :deep(table th) {
  padding: 0 !important;
}

.water-line-table :deep(table td) {
  border-bottom-width: 0 !important;
}
.data-table :deep(th) {
  text-align: left;
  font-size: 0.75rem;
  font-family: Roboto, sans-serif;
  font-weight: bold !important;
  color: rgba(0, 0, 0, 0.6);
}
</style>
